import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle,
  Warning,
  XCircle,
  Clock,
  Buildings,
  User,
  Lock,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import api from '../services/api';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  accepted: { label: 'Already Accepted', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  expired: { label: 'Expired', color: 'bg-slate-100 text-slate-500', icon: Warning },
  revoked: { label: 'Revoked', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const AcceptInvitePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const loadInvitation = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/invitations/code/${code}`);
      setInvitation(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid invitation link');
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    loadInvitation();
  }, [loadInvitation]);

  const handleAccept = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/invitations/accept', { code, password });
      toast.success('Account created successfully! Please log in.');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 text-center">
            <XCircle size={64} className="mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Invalid Invitation</h2>
            <p className="text-slate-500 mb-6">
              {error || 'This invitation link is not valid or has expired.'}
            </p>
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[invitation.status] || STATUS_CONFIG.pending;
  const isPending = invitation.status === 'pending';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Buildings size={32} className="text-indigo-600" />
          </div>
          <CardTitle className="text-2xl">Join {invitation.agency_name}</CardTitle>
          <CardDescription>
            You've been invited to join as a <strong>{invitation.role}</strong>
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Invitation Details */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-3">
            <div className="flex items-center gap-3">
              <User size={18} className="text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Name</p>
                <p className="font-medium">{invitation.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <EnvelopeSimple size={18} className="text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="font-medium">{invitation.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User size={18} className="text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Invited by</p>
                <p className="font-medium">{invitation.invited_by_name}</p>
              </div>
            </div>
            {invitation.message && (
              <div className="pt-3 border-t border-slate-200">
                <p className="text-sm text-slate-600 italic">"{invitation.message}"</p>
              </div>
            )}
          </div>

          {/* Status Badge */}
          <div className="flex justify-center mb-6">
            <Badge className={`${statusConfig.color} text-sm px-4 py-1`}>
              <statusConfig.icon size={14} className="mr-1" />
              {statusConfig.label}
            </Badge>
          </div>

          {isPending ? (
            <form onSubmit={handleAccept} className="space-y-4">
              <div>
                <Label htmlFor="password">Create Password</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="pl-10"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    className="pl-10"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} className="mr-2" />
                    Accept & Create Account
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center">
              {invitation.status === 'accepted' ? (
                <p className="text-slate-500 mb-4">
                  This invitation has already been accepted. Please log in to continue.
                </p>
              ) : invitation.status === 'expired' ? (
                <p className="text-slate-500 mb-4">
                  This invitation has expired. Please contact your administrator for a new invitation.
                </p>
              ) : (
                <p className="text-slate-500 mb-4">
                  This invitation is no longer valid.
                </p>
              )}
              <Button onClick={() => navigate('/login')}>Go to Login</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitePage;
